import {
	AuthenticationUser,
	useGuestQuotePreviewLoginMutation,
	useRefreshTokenMutation,
} from '@codezo/graphql';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { formatLoginUser } from '../../lib/format-auth-user';
import { guestQuotePath, startGuestPreviewSession } from '../../lib/guest-session';
import { GuestNotice } from './GuestAuthChallenge';
import { GuestCard } from './GuestCard';

/**
 * Landing page for an admin's "Preview as guest" link.
 *
 * A sibling of `/guest-accept`, not a mode of it: that page asks for an email
 * and a code, and a preview has an answer to neither — the token was minted for
 * an already-authenticated admin and stands in for both steps. What happens
 * after is identical, deliberately: same session, same routes, same screens.
 *
 * Nothing here is retryable. The token is single-use and expires in minutes, so
 * every failure — expired, already used, invalid — is fixed in the admin portal
 * by clicking the button again, and nowhere else.
 */
export function GuestPreviewPage() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { setUser, setAccessToken } = useAuth();

	const token = searchParams.get('token');
	const [error, setError] = useState<string | null>(null);

	const [previewLogin] = useGuestQuotePreviewLoginMutation();
	const [refreshToken] = useRefreshTokenMutation();

	/* The token is burned on first use, so a second call — StrictMode's double
	   effect, a re-render, a stray dependency change — reports "already used" on
	   a link that worked. A ref rather than state: it has to be set before the
	   second invocation can read it. */
	const redeemed = useRef(false);

	useEffect(() => {
		if (!token || redeemed.current) return;
		redeemed.current = true;

		(async () => {
			try {
				const { data } = await previewLogin({ variables: { input: { token } } });
				const res = data?.guestQuotePreviewLogin;
				if (!res?.success || !res.token || !res.orderQuoteId) {
					setError(res?.message || 'This preview link is no longer valid.');
					return;
				}

				const orderQuoteId = res.orderQuoteId;
				startGuestPreviewSession(orderQuoteId);
				setAccessToken(res.token);

				// A refresh cookie is set too, so trade it for the user record and
				// land in exactly the state a verified guest is in — otherwise the
				// preview would differ from the thing it is previewing.
				const { data: refreshed } = await refreshToken();
				const refreshedUser = refreshed?.refreshToken?.user;
				if (!refreshedUser) {
					setError('The preview session could not be started. Try the button again.');
					return;
				}
				setUser(formatLoginUser(refreshedUser as AuthenticationUser));
				setAccessToken(refreshed.refreshToken.token);
				navigate(guestQuotePath(orderQuoteId), { replace: true });
			} catch (err) {
				setError(
					err instanceof Error ? err.message : 'This preview link could not be opened.'
				);
			}
		})();
	}, [token, previewLogin, refreshToken, setUser, setAccessToken, navigate]);

	if (!token) {
		return (
			<GuestCard>
				<GuestNotice
					title="This preview link is incomplete"
					detail="Open it from the quote in the admin portal — the link carries a token this page needs."
				/>
			</GuestCard>
		);
	}

	if (error) {
		return (
			<GuestCard>
				<GuestNotice
					title={error}
					detail="Preview links are single-use and expire after a few minutes. Press “Preview as guest” on the quote again for a fresh one."
				/>
			</GuestCard>
		);
	}

	return (
		<div className="flex h-full items-center justify-center gap-2 text-sm text-text-3">
			<Loader2 size={15} className="animate-spin" />
			Opening the guest view…
		</div>
	);
}
